import React, { useLayoutEffect, useState, useRef } from "react";

/** Components */
import { Box, Tabs, Tab, ButtonGroup, Button, Tooltip, Table, TableRow, TableCell, TableContainer, TableBody, TableHead } from "@mui/material";

/** Icons */
import AddTaskIcon from "@mui/icons-material/AddTask";
import RepeatOneIcon from "@mui/icons-material/RepeatOne";

/** Api */
import { getERPInvoiceByID, getERPInvoicePDF, sendERPInvoiceByID } from "./../../../services/api";

/** Hook */
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useSnackbar } from "notistack";

export default function ErpInvDetail({ data = "", invoiceid, onUpdate }) {
  // boiler plate
  const [t] = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const isMountedRef = useRef(true);
  const { enqueueSnackbar } = useSnackbar();

  // state
  const [dataState, setData] = useState(data);
  const [tab, setTab] = useState(location.state?.tab || 0);
  const [pdf, setPDF] = useState("loading");

  // method
  const handleSendErp = () => {
    sendERPInvoiceByID(invoiceid).then((res) => {
      if (res.code === 200) {
        enqueueSnackbar(res.message, { variant: "success" });
      } else {
        enqueueSnackbar(res.message, { variant: "error" });
      }
    });
  };

  useLayoutEffect(() => {
    isMountedRef.current = true;
    setData(null);
    if (!invoiceid) return;

    getERPInvoiceByID(invoiceid).then((res) => {
      if (!isMountedRef.current) return; // Prevent state update if unmounted
      if (res.code === 200) setData(res.data);
      else history.replace(`/erpinv/${location.search}`);
    });

    getERPInvoicePDF(invoiceid).then((res) => {
      if (!isMountedRef.current) return; // Prevent state update if unmounted
      if (res.code === 200) {
        setPDF(res.data);
      } else if (res.code === 403) {
        setPDF(null);
      } else setPDF(null);
    });

    return () => {
      isMountedRef.current = false; // Mark component as unmounted
    };
  }, [history, invoiceid, location.search]);

  return (
    <div>
      <div style={{ marginBottom: 20, backgroundColor: "#b3deff", position: "relative", height: 50 }}>
        <Box display="flex">
          <Tabs value={tab} onChange={(e, newValue) => setTab(newValue)}>
            <Tab value={0} label="Thông tin hóa đơn" />
            <Tab value={1} label="Xem hoá đơn dạng pdf" />
          </Tabs>
        </Box>
        <ButtonGroup style={{ height: 50, position: "absolute", top: 0, right: 0 }}>
          {["PUBLISHED", "APPROVED", "ADJUSTED"].includes(dataState?.status) && (
            <Button variant="outlined" color="primary" onClick={() => history.push({ pathname: `/erpinv/adj/${dataState.id}`, state: dataState })}>
              <Tooltip title="Phát hành hoá đơn điều chỉnh">
                <AddTaskIcon />
              </Tooltip>
            </Button>
          )}
          {["PUBLISHED", "APPROVED", "REPLACED"].includes(dataState?.status) && (
            <Button variant="outlined" color="primary" onClick={() => history.push({ pathname: `/erpinv/replace/${dataState.id}`, state: dataState })}>
              <Tooltip title="Phát hành hoá đơn thay thế">
                <RepeatOneIcon />
              </Tooltip>
            </Button>
          )}
        </ButtonGroup>
      </div>
      <div hidden={tab === 0}>{pdf && pdf !== "loading" ? <iframe title="pdf" width="100%" style={{ height: "calc(100vh - 185px)" }} src={pdf} /> : <div>Không có File PDF</div>}</div>
      <div hidden={tab !== 0}>
        {dataState && (
          <>
            <div align="left" style={{ marginBottom: 0 }}>
              <b>
                {t("Thông tin cơ bản")}: {t(dataState?.status)}
              </b>
            </div>
            <TableContainer>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell align="left">
                      Mẫu số: {dataState.pattern} - {dataState.seri}
                    </TableCell>
                    <TableCell align="left">Số hóa đơn: {dataState.code}</TableCell>
                    <TableCell align="left">Ngày phát hành: {dataState.billDate && moment(dataState.billDate).format("DD/MM/YYYY")}</TableCell>
                    <TableCell align="left" colSpan={1}>
                      Mã tra cứu: {dataState.fkey}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align="left">Khách hàng: {dataState.customerName}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align="left" colSpan={1}>
                      Mã số thuế: {dataState.tax}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align="left" colSpan={1}>
                      Địa chỉ: {dataState.addr}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align="left" colSpan={1}>
                      Khu vực bán: {dataState.saleOffice}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align="left" colSpan={1}>
                      Ghi chú: {dataState.remark}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <div align="left" style={{ margin: "10px 0 10px" }}>
              <b>{t("Thông tin hàng hóa")}</b>
            </div>
            <TableContainer style={{ marginBottom: 20 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                      STT
                    </TableCell>
                    <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                      Tên hàng hoá
                    </TableCell>
                    <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                      Đơn vị tính
                    </TableCell>
                    <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                      Số lượng
                    </TableCell>
                    <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                      Đơn giá
                    </TableCell>
                    <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                      Tiền tệ
                    </TableCell>
                    <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                      Thành tiền
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dataState?.products?.map((item, id) => (
                    <TableRow key={id}>
                      <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                        {id + 1}
                      </TableCell>
                      <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                        {item.productName}
                      </TableCell>
                      <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                        {item.UoM}
                      </TableCell>
                      <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                        {item.invoiceQty ? Math.round(item.invoiceQty).toLocaleString() : "0"}
                      </TableCell>
                      <TableCell style={{ border: "0.5px solid lightgray" }} align="right">
                        {dataState.currency === "VND"
                          ? Math.round(item.price || 0).toLocaleString("vi-VN")
                          : (item.price || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                        {dataState.currency}
                      </TableCell>
                      <TableCell style={{ border: "0.5px solid lightgray" }} align="right">
                        {dataState.currency === "VND"
                          ? Math.round(item.amount || 0).toLocaleString("vi-VN")
                          : (item.amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell style={{ border: "0.5px solid lightgray" }} colSpan={6} align="left">
                      Tổng tiền
                    </TableCell>
                    <TableCell style={{ border: "0.5px solid lightgray" }} align="right">
                      {dataState.currency === "VND"
                        ? Math.round(dataState.subTotal || 0).toLocaleString("vi-VN")
                        : (dataState.subTotal || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ border: "0.5px solid lightgray" }} colSpan={5} align="left">
                      Thuế
                    </TableCell>
                    <TableCell style={{ border: "0.5px solid lightgray" }}>{dataState.VAT === -1 ? "\\" : dataState.VAT + " (%)"} </TableCell>
                    <TableCell style={{ border: "0.5px solid lightgray" }} align="right">
                      {dataState.VATAtm === -1
                        ? "\\"
                        : dataState.currency === "VND"
                        ? Math.round(dataState.VATAtm || 0).toLocaleString("vi-VN")
                        : (dataState.VATAtm || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ border: "0.5px solid lightgray" }} colSpan={6} align="left">
                      Tổng tiền (bao gồm Thuế)
                    </TableCell>
                    <TableCell style={{ border: "0.5px solid lightgray" }} align="right">
                      {dataState.currency === "VND"
                        ? Math.round(dataState.invTotal || 0).toLocaleString("vi-VN")
                        : (dataState.invTotal || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ border: "0.5px solid lightgray" }} colSpan={7} align="left">
                      Bằng chữ: {dataState.inwords}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </div>
      {["ADJUST_UP", "ADJUST_DOWN", "ADJUST_INFO", "REPLACED"].includes(dataState?.status) && (
        <div style={{ position: "relative" }}>
          <Box display="flex">
            <Button variant="outlined" color="primary" onClick={() => handleSendErp()}>
              DUYỆT
            </Button>
          </Box>
        </div>
      )}
    </div>
  );
}
